// ── Stitch transaction → Tlhakiso row ──
//
// Maps a Stitch transaction node onto the SAME normalised row shape the CSV/XLSX/PDF
// upload path produces ({date, description, amount, balance}), so synced transactions
// flow through the existing categorisation + review + posting pipeline unchanged.
//
// Stitch bank-account transaction node (GraphQL `Transaction`):
//   { __typename, id, date, amount, reference, description, runningBalance }
// Unlike Mono, Stitch amounts are in MAJOR units (Rand) and already SIGNED
// (credit = positive, debit = negative), so no ÷100 and no debit/credit flag needed.
// Sign convention for Tlhakiso rows matches: positive = money IN, negative = money OUT.

function toNumber(v) {
  // Stitch may return amount as a string, a number, or a { quantity } money object.
  if (v == null) return 0;
  if (typeof v === "object") v = v.quantity ?? v.amount ?? 0;
  const n = Number(v);
  return isFinite(n) ? n : 0;
}

// One Stitch transaction node → one normalised row. Returns null for anything
// unparseable so callers can filter it out rather than posting a garbage line.
export function normalizeStitchTransaction(txn, fileLabel) {
  if (!txn) return null;
  const amount = toNumber(txn.amount);
  const date = (txn.date || "").slice(0, 10); // ISO YYYY-MM-DD
  if (!date) return null;
  const description = (txn.description || txn.reference || "").trim();
  return {
    date,
    description,
    amount,
    balance: txn.runningBalance != null ? toNumber(txn.runningBalance) : null,
    // Provenance for idempotency + audit; double-underscore matches the upload path.
    // __bankTxnId is the provider-neutral id the posting/dedup pipeline keys on.
    __source: fileLabel || "Stitch bank feed",
    __bankTxnId: txn.id || null,
    __reference: txn.reference || null,
  };
}

// Normalise + sort a whole page of Stitch transactions oldest-first (the order the
// review UI and duplicate detection expect).
export function normalizeStitchTransactions(transactions, fileLabel) {
  return (transactions || [])
    .map((t) => normalizeStitchTransaction(t, fileLabel))
    .filter(Boolean)
    .sort((a, b) => new Date(a.date) - new Date(b.date));
}
